import React, { useEffect, useState } from 'react';
import {
  X, BarChart2, FileSpreadsheet, CheckCircle2, XCircle, UploadCloud,
  Clock, ChevronDown, ChevronUp, MapPin,
  Layers, Activity, CalendarClock, AlertTriangle,
} from 'lucide-react';
import { getUploadMetrics, type UploadMetricEntry } from '../../services/uploadMetricsService';

interface UploadDashboardProps {
  isOpen: boolean;
  onClose: () => void;
  colorMode: 'light' | 'dark';
}

const UploadDashboard: React.FC<UploadDashboardProps> = ({ isOpen, onClose }) => {
  const [metrics, setMetrics] = useState<UploadMetricEntry[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) { setMetrics(getUploadMetrics()); setExpandedId(null); }
  }, [isOpen]);

  if (!isOpen) return null;

  /* ── design tokens (light theme) ── */
  const bg         = '#f0f4f8';
  const modalBg    = '#ffffff';
  const panelBg    = '#f7f9fc';
  const rowHover   = '#eef2fa';
  const border     = '#dde3ee';
  const borderFaint= '#e8ecf5';
  const textPri    = '#1a2540';
  const textSec    = '#6b7a99';
  const textMuted  = '#9aa5bc';
  const green      = '#16a34a';
  const greenDim   = 'rgba(22,163,74,0.08)';
  const greenBdr   = 'rgba(22,163,74,0.2)';
  const red        = '#dc2626';
  const redDim     = 'rgba(220,38,38,0.07)';
  const redBdr     = 'rgba(220,38,38,0.2)';
  const blue       = '#2563eb';
  const blueDim    = 'rgba(37,99,235,0.08)';
  const blueBdr    = 'rgba(37,99,235,0.18)';
  const amber      = '#d97706';

  const fmt = (iso: string) => {
    try {
      const d = new Date(iso);
      return {
        date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      };
    } catch { return { date: '—', time: '' }; }
  };
  const fmtDur = (ms: number) => ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;

  const aggFiles   = metrics.length;
  const aggRows    = metrics.reduce((s, m) => s + m.totalRows, 0);
  const aggSuccess = metrics.reduce((s, m) => s + m.loadedRecords, 0);
  const aggFailed  = metrics.reduce((s, m) => s + m.failedRecords, 0);

  /* ── shared pill style ── */
  const pill = (val: number, color: string, dim: string, bdr: string) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      background: dim, border: `1px solid ${bdr}`,
      color, fontSize: '12px', fontWeight: 700,
      padding: '3px 10px', borderRadius: '999px',
    }}>
      {val.toLocaleString()}
    </span>
  );

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 11000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(15,23,58,0.4)', backdropFilter: 'blur(6px)',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        display: 'flex', flexDirection: 'column',
        width: '860px', maxWidth: 'calc(100vw - 24px)',
        height: 'calc(100vh - 160px)', maxHeight: 'calc(100vh - 160px)',
        background: modalBg,
        borderRadius: '18px',
        border: `1px solid ${border}`,
        boxShadow: '0 24px 64px rgba(15,23,58,0.18), 0 4px 16px rgba(15,23,58,0.08)',
        overflow: 'hidden',
      }}>

        {/* ═══ HEADER ═══ */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: 'linear-gradient(135deg, #0f2156 0%, #1e3a8a 100%)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: 40, height: 40, borderRadius: '12px',
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.22)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BarChart2 size={20} color='#ffffff' />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#ffffff' }}>Dashboard</p>
              <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.52)' }}>Load history &amp; metrics</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={onClose}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.22)',
                color: '#ffffff', cursor: 'pointer', width: 32, height: 32, borderRadius: '8px',
              }}
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* ═══ BODY ═══ */}
        <div style={{ flex: 1, overflowY: 'auto', background: bg, padding: '20px' }}>

          {metrics.length === 0 ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: '14px', padding: '80px 24px', textAlign: 'center',
            }}>
              <div style={{
                width: 64, height: 64, borderRadius: '16px',
                background: blueDim, border: `1px solid ${blueBdr}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Activity size={28} color={blue} />
              </div>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: textPri }}>No uploads yet</p>
              <p style={{ margin: 0, fontSize: '12px', color: textSec, maxWidth: 260, lineHeight: 1.8 }}>
                Complete an Excel upload workflow to see metrics here.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* ─── KPI ROW ─── */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                {/* Files */}
                <div style={{
                  background: panelBg, border: `1px solid ${border}`,
                  borderRadius: '14px', padding: '16px 18px',
                  display: 'flex', alignItems: 'center', gap: '14px',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '10px',
                    background: blueDim, border: `1px solid ${blueBdr}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Layers size={18} color={blue} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: blue, lineHeight: 1 }}>{aggFiles}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: textSec }}>Files uploaded</p>
                  </div>
                </div>
                {/* Addresses */}
                <div style={{
                  background: panelBg, border: `1px solid ${border}`,
                  borderRadius: '14px', padding: '16px 18px',
                  display: 'flex', alignItems: 'center', gap: '14px',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '10px',
                    background: 'rgba(107,122,153,0.08)', border: `1px solid ${border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <MapPin size={18} color={textSec} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: textPri, lineHeight: 1 }}>{aggRows.toLocaleString()}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: textSec }}>Addresses submitted</p>
                  </div>
                </div>
                {/* Success */}
                <div style={{
                  background: panelBg, border: `1px solid ${border}`,
                  borderRadius: '14px', padding: '16px 18px',
                  display: 'flex', alignItems: 'center', gap: '14px',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '10px',
                    background: greenDim, border: `1px solid ${greenBdr}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <CheckCircle2 size={18} color={green} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: green, lineHeight: 1 }}>{aggSuccess.toLocaleString()}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'rgba(22,163,74,0.75)' }}>Records loaded</p>
                  </div>
                </div>
                {/* Failed */}
                <div style={{
                  background: aggFailed > 0 ? 'rgba(248,113,113,0.06)' : panelBg,
                  border: `1px solid ${aggFailed > 0 ? redBdr : border}`,
                  borderRadius: '14px', padding: '16px 18px',
                  display: 'flex', alignItems: 'center', gap: '14px',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '10px',
                    background: aggFailed > 0 ? redDim : 'rgba(107,122,153,0.08)',
                    border: `1px solid ${aggFailed > 0 ? redBdr : border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <XCircle size={18} color={aggFailed > 0 ? red : textSec} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: aggFailed > 0 ? red : textSec, lineHeight: 1 }}>{aggFailed.toLocaleString()}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: aggFailed > 0 ? 'rgba(220,38,38,0.7)' : textSec }}>Records failed</p>
                  </div>
                </div>
              </div>

              {/* ─── UNIFIED UPLOAD TABLE ─── */}
              <div style={{
                background: panelBg, border: `1px solid ${border}`, borderRadius: '16px',
                overflow: 'hidden',
              }}>
                {/* panel header */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 20px',
                  borderBottom: `1px solid ${border}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UploadCloud size={14} color={textSec} />
                    <span style={{ fontSize: '12px', fontWeight: 700, color: textSec, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Upload History
                    </span>
                    <span style={{
                      background: blueDim, border: `1px solid ${blueBdr}`,
                      color: blue, fontSize: '10px', fontWeight: 700,
                      padding: '2px 8px', borderRadius: '999px',
                    }}>
                      {metrics.length}
                    </span>
                  </div>
                  <span style={{ fontSize: '11px', color: textMuted }}>Last 10 workflows</span>
                </div>

                {/* column headings */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 140px 70px 70px 70px 68px 32px',
                  padding: '8px 20px',
                  borderBottom: `1px solid ${borderFaint}`,
                  background: 'rgba(37,99,235,0.04)',
                }}>
                  {['File', 'Uploaded', 'Sent', 'Loaded', 'Failed', 'Duration', ''].map(h => (
                    <span key={h} style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: textMuted }}>{h}</span>
                  ))}
                </div>

                {/* rows */}
                {metrics.map((entry, idx) => {
                  const hasErrors = (entry.failedDetails?.length ?? 0) > 0;
                  const isExp = expandedId === entry.id;
                  const dt = fmt(entry.uploadedAt);
                  const allOk = entry.failedRecords === 0;

                  return (
                    <React.Fragment key={entry.id}>
                      {/* main row */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 140px 70px 70px 70px 68px 32px',
                        padding: '13px 20px', alignItems: 'center',
                        borderBottom: isExp ? `1px solid ${redBdr}` : `1px solid ${idx < metrics.length - 1 || isExp ? borderFaint : 'transparent'}`,
                        background: isExp ? 'rgba(220,38,38,0.04)' : 'transparent',
                        cursor: hasErrors ? 'pointer' : 'default',
                        transition: 'background 0.15s',
                      }}
                        onMouseEnter={e => { if (!isExp) (e.currentTarget as HTMLDivElement).style.background = rowHover; }}
                        onMouseLeave={e => { if (!isExp) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                        onClick={() => hasErrors && setExpandedId(isExp ? null : entry.id)}
                      >
                        {/* file name */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: '8px', flexShrink: 0,
                            background: blueDim, border: `1px solid ${blueBdr}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <FileSpreadsheet size={14} color={blue} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: textPri, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {entry.fileName}
                            </p>
                            <p style={{ margin: '2px 0 0', fontSize: '10px', color: textMuted, display: 'flex', alignItems: 'center', gap: '3px' }}>
                              {allOk
                                ? <><CheckCircle2 size={9} color={green} /><span style={{ color: 'rgba(22,163,74,0.8)' }}>All records loaded</span></>
                                : <><AlertTriangle size={9} color={amber} /><span style={{ color: 'rgba(217,119,6,0.8)' }}>{entry.failedRecords} failed</span></>
                              }
                            </p>
                          </div>
                        </div>

                        {/* date/time */}
                        <div>
                          <p style={{ margin: 0, fontSize: '12px', color: textPri }}>{dt.date}</p>
                          <p style={{ margin: '2px 0 0', fontSize: '10px', color: textSec, display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <CalendarClock size={9} />{dt.time}
                          </p>
                        </div>

                        {/* sent */}
                        <span style={{ fontSize: '13px', fontWeight: 700, color: textPri }}>{entry.totalRecords.toLocaleString()}</span>

                        {/* loaded */}
                        {pill(entry.loadedRecords, green, greenDim, greenBdr)}

                        {/* failed */}
                        {entry.failedRecords > 0
                          ? pill(entry.failedRecords, red, redDim, redBdr)
                          : <span style={{ fontSize: '12px', color: textMuted }}>—</span>
                        }

                        {/* duration */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Clock size={11} color={textSec} />
                          <span style={{ fontSize: '12px', color: textSec }}>{fmtDur(entry.durationMs)}</span>
                        </div>

                        {/* expand chevron */}
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          {hasErrors
                            ? isExp
                              ? <ChevronUp size={14} color={red} />
                              : <ChevronDown size={14} color={textMuted} />
                            : null
                          }
                        </div>
                      </div>

                      {/* ── expanded error drawer ── */}
                      {isExp && (
                        <div style={{ background: '#ffffff', borderTop: `1px solid ${border}` }}>
                          {/* drawer header */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '70px 160px 1fr',
                            padding: '8px 20px',
                            background: '#f8fafc',
                            borderBottom: `1px solid ${border}`,
                          }}>
                            {['Row #', 'NPI', 'Error Message'].map(h => (
                              <span key={h} style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: textMuted }}>{h}</span>
                            ))}
                          </div>
                          {/* drawer rows */}
                          <div style={{ maxHeight: '260px', overflowY: 'scroll' }}>
                            {entry.failedDetails!.map((err, ei) => (
                              <div key={ei} style={{
                                display: 'grid',
                                gridTemplateColumns: '70px 160px 1fr',
                                padding: '9px 20px', alignItems: 'start',
                                borderBottom: `1px solid ${borderFaint}`,
                                background: ei % 2 === 0 ? '#f8fafc' : '#ffffff',
                              }}>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: textSec }}>#{err.rowNumber}</span>
                                <span style={{ fontSize: '12px', color: textPri, fontFamily: 'monospace', paddingRight: '12px', wordBreak: 'break-all' }}>{err.memberId || '—'}</span>
                                <span style={{ fontSize: '12px', color: red, lineHeight: 1.6, wordBreak: 'break-word' }}>{err.message || 'Load failed'}</span>
                              </div>
                            ))}
                          </div>
                          {/* drawer footer */}
                          <div style={{
                            padding: '8px 20px',
                            borderTop: `1px solid ${border}`,
                            background: '#f8fafc',
                            display: 'flex', alignItems: 'center', gap: '6px',
                          }}>
                            <AlertTriangle size={11} color={amber} />
                            <span style={{ fontSize: '11px', color: textSec }}>
                              {entry.failedDetails!.length} failed record{entry.failedDetails!.length !== 1 ? 's' : ''} for {entry.fileName}
                            </span>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ═══ FOOTER ═══ */}
        <div style={{
          padding: '10px 20px',
          borderTop: `1px solid ${border}`,
          background: modalBg,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: '11px', color: textMuted, display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Clock size={10} color={textMuted} />
            Persisted across sessions &mdash; up to 10 uploads
          </span>
          {metrics.length > 0 && (
            <span style={{ fontSize: '11px', color: textMuted }}>
              Click a row with failures to view error details
            </span>
          )}
        </div>

      </div>
    </div>
  );
};

export default UploadDashboard;
